@if (liveClass) {
<div class="space-y-4 mb-12">
  @for (item of liveClass.meetings; track item) {
    @let isAccessible = isMeetingAccessible(item);
    @let isPending = isMeetingPending(item);
    @let countdown = getMeetingCountdown(item);
    @let nextEventDate = getNextMeetingDate(item);

    <div
      class="daisy-card daisy-card-bordered bg-base-100 shadow-sm transition-all duration-300"
      [class.border-primary]="isAccessible && isSingleEventSubscriber()"
      [class.border-base-300]="isAccessible && !isSingleEventSubscriber()"
      [class.bg-base-200/50]="!isAccessible && !isPending"
      [class.border-warning/40]="!isAccessible || isPending"
      [class.opacity-90]="!isAccessible && !isPending"
    >
      <div class="daisy-card-body p-4 sm:p-5 md:p-6">
        <div
          class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
        >
          <div class="flex-1 min-w-0">
            <div class="flex items-start gap-3 mb-2">
              @if (isPending) {
                <div
                  class="w-3 h-3 bg-warning rounded-full mt-1.5 shrink-0"
                  title="Payment Under Review"
                ></div>
              } @else if (isAccessible) {
                <div
                  class="w-3 h-3 bg-success rounded-full mt-1.5 shrink-0"
                  title="Accessible Session"
                ></div>
              } @else {
                <div
                  class="w-3 h-3 bg-warning rounded-full mt-1.5 shrink-0"
                  title="Restricted Session"
                ></div>
              }
              <div class="flex-1 min-w-0">
                <div class="flex flex-wrap items-center gap-2 mb-1.5">
                  <h4 class="text-lg md:text-xl font-bold text-base-content break-words">
                    {{ item.name }}
                  </h4>

                  @if (isPending) {
                    <span class="daisy-badge daisy-badge-warning daisy-badge-sm font-semibold gap-1">
                      <i class="fa-solid fa-clock text-xs"></i>
                      Verification Pending
                    </span>
                  } @else if (getSinglePassForMeeting(item); as pass) {
                    <button
                      type="button"
                      (click)="openPassModal(item)"
                      class="daisy-badge daisy-badge-success daisy-badge-sm font-bold text-success-content gap-1 cursor-pointer hover:opacity-90 transition-opacity"
                      title="Click to view Pass details"
                    >
                      <i class="fa-solid fa-ticket text-xs"></i>
                      Your Pass
                      <i class="fa-solid fa-circle-info text-[10px] ml-0.5"></i>
                    </button>
                    @if (pass.targetEvent?.eventDate) {
                      <span class="daisy-badge daisy-badge-outline daisy-badge-sm text-xs font-medium">
                        {{ pass.targetEvent?.eventDate | date: 'mediumDate' }}
                      </span>
                    }
                  } @else if (!hasFullCourseAccess) {
                    <span class="daisy-badge daisy-badge-warning daisy-badge-outline daisy-badge-sm font-semibold gap-1">
                      <i class="fa-solid fa-lock text-xs"></i>
                      Not in Your Pass
                    </span>
                    @if (isFreeMeeting(item)) {
                      <span class="daisy-badge daisy-badge-ghost daisy-badge-sm text-xs">
                        Free
                      </span>
                    }
                  }
                </div>

                <div
                  class="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-base-content/70 mb-2.5"
                >
                  <div class="flex items-center gap-1.5">
                    <i class="fa-solid fa-calendar text-primary"></i>
                    <span class="font-medium">
                      {{
                        getMeetingInterval(
                          item,
                          liveClass.details.startDate,
                          liveClass.details.endDate
                        )
                      }}
                    </span>
                  </div>
                  <div class="flex items-center gap-1.5">
                    <i class="fa-solid fa-clock text-secondary"></i>
                    <span class="font-medium">
                      {{
                        getStartDate(
                          item,
                          liveClass.details.startDate,
                          liveClass.details.endDate
                        )[0] | date: 'shortTime'
                      }}
                      –
                      {{
                        getStartDate(
                          item,
                          liveClass.details.startDate,
                          liveClass.details.endDate
                        )[1] | date: 'shortTime'
                      }}
                    </span>
                  </div>
                  @if (nextEventDate) {
                    <div class="flex items-center gap-1.5 text-xs text-primary font-medium">
                      <i class="fa-regular fa-calendar-check"></i>
                      <span>Next Session: {{ nextEventDate | date: 'EEE, MMM d' }}</span>
                    </div>
                  }
                </div>

                <p class="text-xs md:text-sm text-base-content/70 leading-relaxed break-words">
                  {{
                    getMeetingDescription(
                      item,
                      liveClass.details.startDate,
                      liveClass.details.endDate
                    )
                  }}
                </p>

                @if (!isAccessible && !isPending) {
                  <div class="daisy-alert bg-warning/10 border border-warning/25 text-base-content text-xs py-2 px-3 mt-3 flex items-center gap-2 rounded-box">
                    <i class="fa-solid fa-circle-exclamation text-warning text-sm shrink-0"></i>
                    <span class="text-base-content/80">
                      This session is not included in your single class pass. Purchase an individual pass or upgrade to attend.
                    </span>
                  </div>
                }
              </div>
            </div>
          </div>

          <!-- Actions Column -->
          <div class="shrink-0 flex flex-wrap items-center gap-2 pt-3 border-t border-base-200/80 lg:border-t-0 lg:pt-0 w-full lg:w-auto">
            @if (isPending) {
              <a
                routerLink="/invoices"
                class="daisy-btn daisy-btn-warning daisy-btn-outline daisy-btn-sm gap-1.5 shadow-sm w-full sm:w-auto justify-center"
              >
                <i class="fa-solid fa-clock"></i>
                <span>Verification Pending</span>
              </a>
            } @else if (isAccessible) {
              @if (item.shareLinkId) {
                @if (isMeetingConfigured(item)) {
                  <button
                    type="button"
                    (click)="copyShareLinkId(item.shareLinkId, item.provider)"
                    class="daisy-btn daisy-btn-outline daisy-btn-sm gap-2 w-full sm:w-auto justify-center"
                    title="Copy Meeting Link"
                  >
                    <i class="fa-solid fa-copy"></i>
                    <span>Copy Link</span>
                  </button>

                  <!-- Dynamic Join / Countdown Button -->
                  @if (countdown?.status === 'LIVE') {
                    <button
                      type="button"
                      (click)="joinMeeting(item)"
                      class="daisy-btn daisy-btn-primary daisy-btn-sm gap-2 shadow-md animate-pulse w-full sm:w-auto justify-center"
                      title="Class is live now! Click to join"
                    >
                      <span class="w-2 h-2 rounded-full bg-error animate-ping"></span>
                      <i class="fa-solid fa-video"></i>
                      <span>Join Live</span>
                    </button>
                  } @else if (countdown?.status === 'STARTING_SOON') {
                    <button
                      type="button"
                      (click)="joinMeeting(item)"
                      class="daisy-btn daisy-btn-primary daisy-btn-sm gap-2 shadow-sm w-full sm:w-auto justify-center"
                      title="Starting soon! Click to join"
                    >
                      <i class="fa-solid fa-video"></i>
                      <span>Join ({{ countdown?.countdownText }})</span>
                    </button>
                  } @else if (countdown?.isSessionDay && countdown?.status === 'UPCOMING') {
                    <button
                      type="button"
                      class="daisy-btn daisy-btn-outline daisy-btn-primary daisy-btn-sm gap-2 opacity-80 cursor-not-allowed w-full sm:w-auto justify-center"
                      disabled
                      title="Session is scheduled for today. Opens 15 minutes before start."
                    >
                      <i class="fa-solid fa-clock"></i>
                      <span>Starts in {{ countdown?.countdownText }}</span>
                    </button>
                  } @else if (!countdown?.isSessionDay && countdown?.status === 'UPCOMING') {
                    <button
                      type="button"
                      class="daisy-btn daisy-btn-neutral daisy-btn-outline daisy-btn-sm gap-2 opacity-60 cursor-not-allowed w-full sm:w-auto justify-center"
                      disabled
                      title="Not on session day. Countdown: {{ countdown?.countdownText }}"
                    >
                      <i class="fa-solid fa-clock text-primary"></i>
                      <span>{{ countdown?.countdownText }}</span>
                    </button>
                  } @else if (countdown?.status === 'ENDED') {
                    <button
                      type="button"
                      class="daisy-btn daisy-btn-ghost daisy-btn-sm gap-1.5 opacity-50 cursor-not-allowed w-full sm:w-auto justify-center"
                      disabled
                      title="Session has ended"
                    >
                      <i class="fa-solid fa-check"></i>
                      <span>Ended</span>
                    </button>
                  } @else {
                    <button
                      type="button"
                      (click)="joinMeeting(item)"
                      class="daisy-btn daisy-btn-primary daisy-btn-sm gap-2 shadow-sm w-full sm:w-auto justify-center"
                    >
                      <i class="fa-solid fa-video"></i>
                      <span>Join Meeting</span>
                    </button>
                  }
                } @else {
                  <button
                    type="button"
                    class="daisy-btn daisy-btn-disabled daisy-btn-sm gap-1 w-full sm:w-auto justify-center"
                    disabled
                    title="Meeting is not yet configured by instructor"
                  >
                    <i class="fa-solid fa-clock"></i>
                    <span>Not Configured</span>
                  </button>
                }
              } @else {
                <div class="daisy-badge daisy-badge-outline text-xs">No Meeting Link</div>
              }
            } @else {
              <!-- Locked session actions -->
              <button
                type="button"
                (click)="onBuyPassForSession(item)"
                class="daisy-btn daisy-btn-primary daisy-btn-sm gap-1.5 shadow-sm w-full sm:w-auto justify-center"
              >
                <i class="fa-solid fa-ticket"></i>
                <span>{{ isFreeMeeting(item) ? 'Get Free Pass' : 'Buy Session Pass' }}</span>
              </button>
            }
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Pass Details Modal -->
  <dialog
    class="daisy-modal"
    [class.daisy-modal-open]="isPassModalOpen"
    id="pass_details_modal"
  >
    <div class="daisy-modal-box max-w-md bg-base-100 text-base-content border border-base-300 shadow-2xl p-4 sm:p-6">
      <div class="flex items-center gap-3 pb-3 border-b border-base-300">
        <div class="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center text-success shrink-0">
          <i class="fa-solid fa-ticket text-lg"></i>
        </div>
        <div class="flex-1 min-w-0">
          <h3 class="font-bold text-base sm:text-lg text-base-content">Single Class Pass</h3>
          <p class="text-xs text-success font-medium">Verified & Active</p>
        </div>
      </div>

      <div class="py-4 space-y-3.5 text-sm">
        <div class="p-3 sm:p-3.5 rounded-lg bg-base-200/60 border border-base-300 space-y-2 text-xs">
          <div class="flex items-start justify-between gap-2">
            <span class="text-base-content/60 font-medium shrink-0">Course:</span>
            <span class="font-bold text-base-content text-right break-words min-w-0 flex-1">{{ selectedPassDetails?.liveClassName }}</span>
          </div>
          <div class="flex items-start justify-between gap-2">
            <span class="text-base-content/60 font-medium shrink-0">Session:</span>
            <span class="font-bold text-primary text-right break-words min-w-0 flex-1">{{ selectedPassDetails?.meetingName }}</span>
          </div>
          @if (selectedPassDetails?.targetEventDate) {
            <div class="flex items-center justify-between gap-2">
              <span class="text-base-content/60 font-medium shrink-0">Date:</span>
              <span class="font-semibold text-base-content text-right">{{ selectedPassDetails?.targetEventDate | date: 'mediumDate' }}</span>
            </div>
          }
          @if (selectedPassDetails?.startTime) {
            <div class="flex items-center justify-between gap-2">
              <span class="text-base-content/60 font-medium shrink-0">Time:</span>
              <span class="font-semibold text-base-content text-right">
                {{ selectedPassDetails?.startTime }}
                @if (selectedPassDetails?.endTime) {
                  – {{ selectedPassDetails?.endTime }}
                }
              </span>
            </div>
          }
        </div>

        <p class="text-xs text-base-content/70 leading-relaxed">
          This pass grants you full access to participate in this specific class session, including live interactive video and discussion.
        </p>

        <div class="daisy-alert bg-primary/5 border border-primary/20 text-xs py-2.5 px-3 flex items-start gap-2.5">
          <i class="fa-solid fa-lightbulb text-primary mt-0.5 shrink-0"></i>
          <span class="text-base-content/80 leading-relaxed">
            Want access to all sessions in this course? You can upgrade anytime and your single pass payment will be credited automatically.
          </span>
        </div>
      </div>

      <div class="daisy-modal-action flex flex-col-reverse sm:flex-row gap-2 pt-2">
        <button
          type="button"
          (click)="closePassModal()"
          class="daisy-btn daisy-btn-ghost daisy-btn-sm w-full sm:w-auto"
        >
          Close
        </button>
        <button
          type="button"
          (click)="navigateToUpgrade()"
          class="daisy-btn daisy-btn-primary daisy-btn-sm gap-1.5 shadow-sm w-full sm:w-auto justify-center"
        >
          <i class="fa-solid fa-arrow-up-right-from-square"></i>
          <span>Upgrade to Full Course</span>
        </button>
      </div>
    </div>
    <form method="dialog" class="daisy-modal-backdrop">
      <button (click)="closePassModal()">close</button>
    </form>
  </dialog>
</div>
}
